/**
 * Fallback list of supported locales, in DropDown format
 * Used if the remote locale list fails to load
 */
const fallbackLocaleList = [
  {value: 'bg-BG', label: 'Bulgarian'},
  {value: 'ca-ES', label: 'Catalan (Spain)'},
  {value: 'cs-CS', label: 'Czech'},
  {value: 'da-DA', label: 'Danish'},
  {value: 'de-DE', label: 'German'},
  {value: 'el-GR', label: 'Greek'},
  {value: 'en-US', label: 'English (US)'},
  {value: 'es-ES', label: 'Spanish'},
  {value: 'et-EE', label: 'Estonian'},
  {value: 'fi-FI', label: 'Finnish'},
  {value: 'fr-FR', label: 'French'},
  {value: 'hu-HU', label: 'Hungarian'},
  {value: 'is-IS', label: 'Icelandic'},
  {value: 'it-IT', label: 'Italian'},
  {value: 'ja-JP', label: 'Japanese'},
  {value: 'ko-KR', label: 'Korean'},
  {value: 'lt-LT', label: 'Lithuanian'},
  {value: 'lv-LV', label: 'Latvian'},
  {value: 'nb-NO', label: 'Norwegian'},
  {value: 'nl-NL', label: 'Dutch'},
  {value: 'pl-PL', label: 'Polish'},
  {value: 'pt-BR', label: 'Portuguese (Brazil)'},
  {value: 'pt-PT', label: 'Portuguese'},
  {value: 'ro-RO', label: 'Romanian'},
  {value: 'ru-RU', label: 'Russian'},
  {value: 'sk-SK', label: 'Slovak'},
  {value: 'sv-SE', label: 'Swedish'},
  {value: 'zh-CN', label: 'Chinese (Mandarin)'},
  {value: 'zh-HK', label: 'Chinese (Cantonese)'},
  {value: 'zh-TW', label: 'Chinese (Taiwanese)'},
];

/**
 * Cache for loaded locale list
 */
let cachedLocaleList = null;
let loadingPromise = null;

/**
 * Load supported locales from remote URL
 * @returns {Promise<Array>} Promise that resolves to array of locale objects
 */
const loadLocaleList = async () => {
  if (cachedLocaleList) {
    return cachedLocaleList;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = fetch('https://storage.googleapis.com/dized-data/tools/supportedLocales.json')
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      // Validate the data structure
      if (!Array.isArray(data)) {
        throw new Error('Invalid locale data format - expected array');
      }
      
      // Validate each locale object has required properties
      const validatedData = data.filter(locale => 
        locale && 
        typeof locale.value === 'string' && 
        typeof locale.label === 'string'
      );
      
      if (validatedData.length === 0) {
        throw new Error('No valid locale entries found');
      }
      
      cachedLocaleList = validatedData;
      return cachedLocaleList;
    })
    .catch(error => {
      console.warn('Failed to load remote locale list, using fallback:', error);
      cachedLocaleList = fallbackLocaleList;
      return cachedLocaleList;
    })
    .finally(() => {
      loadingPromise = null;
    });

  return loadingPromise;
};

/**
 * Get the locale list (loads from remote if not cached)
 * @returns {Promise<Array>} Promise that resolves to array of locale objects
 */
export const getLocaleList = () => {
  return loadLocaleList();
};

/**
 * Get the locale list synchronously (returns fallback if remote not loaded)
 * @returns {Array} Array of locale objects
 */
export const localeList = cachedLocaleList || fallbackLocaleList;

/**
 * Get sorted locale list
 * @returns {Promise<Array>} Promise that resolves to sorted array of locale objects
 */
export const getSortedLocaleList = async () => {
  const list = await getLocaleList();
  return [...list].sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * List of supported locales, sorted by name, in DropDown format
 * Note: This is a fallback for components that expect synchronous access
 */
export const sortedLocaleList = [...fallbackLocaleList].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Get name of a locale
 *
 * @param {string} locale - locale identifier
 * @returns {string}        locale name
 */
export const getLocaleName = locale => {
  // First try the cached list if available
  if (cachedLocaleList) {
    return cachedLocaleList.find(l => l.value === locale)?.label;
  }
  // Fall back to the fallback list
  return fallbackLocaleList.find(l => l.value === locale)?.label;
};

/**
 * Get name of a locale asynchronously (ensures latest data)
 *
 * @param {string} locale - locale identifier
 * @returns {Promise<string>} Promise that resolves to locale name
 */
export const getLocaleNameAsync = async (locale) => {
  const list = await getLocaleList();
  return list.find(l => l.value === locale)?.label;
};
